import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiUrls } from '@/lib/apiClient';
import type { AnnouncementModel } from '@/types/model.types';

const STORAGE_KEY = 'acknowledgedAnnouncments'; // spelling kept for continuity
const CHECK_INTERVAL_MS = 60 * 60_000;

function readAcknowledged(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Port of AnnouncementService. The manual setInterval is replaced by
 * refetchInterval, so polling stops when no component is mounted.
 */
export function useAnnouncements() {
  const query = useQuery({
    queryKey: ['announcements'],
    queryFn: () =>
      api.get<AnnouncementModel[]>(apiUrls.announcement, {
        operation: 'retrieving announcements',
      }),
    refetchInterval: CHECK_INTERVAL_MS,
    staleTime: CHECK_INTERVAL_MS,
  });

  const unacknowledged = useMemo(() => {
    if (!query.data) return [];
    const acknowledged = new Set(readAcknowledged());
    return query.data.filter(
      (announcement) => announcement.active && !acknowledged.has(announcement.announcementId),
    );
  }, [query.data]);

  const acknowledge = useCallback((announcementId: string) => {
    const acknowledged = readAcknowledged();
    if (acknowledged.includes(announcementId)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...acknowledged, announcementId]));
  }, []);

  return { ...query, unacknowledged, acknowledge };
}
